/**
 * Inline Quick Add
 *
 * Handles the inline size/color hover overlay on product cards.
 * - Reads variant data from data-variants JSON attribute
 * - Tracks selected options per overlay instance
 * - On full selection: POSTs to /cart/add.js (AJAX) and dispatches cart update event
 * - Falls back to existing quick-add modal when variant cannot be resolved
 * - Dispatches theme:cart:update so the cart drawer refreshes automatically
 */

(function () {
  'use strict';

  /**
   * CartUpdateEvent name used by the Earth theme's cart drawer.
   * Mirrors ThemeEvents.cartUpdate in events.js.
   */
  const CART_UPDATE_EVENT = 'theme:cart:update';

  /**
   * Initialises all .inline-quick-add overlays that have not yet been bound.
   */
  function initOverlays() {
    document.querySelectorAll('.inline-quick-add:not([data-iqa-bound])').forEach(bindOverlay);
  }

  /**
   * Binds interaction logic to a single inline-quick-add overlay element.
   * @param {HTMLElement} overlay
   */
  function bindOverlay(overlay) {
    overlay.dataset.iqaBound = 'true';

    /** @type {Array<{id: number, options: string[], available: boolean}>} */
    let variants;
    try {
      variants = JSON.parse(overlay.dataset.variants || '[]');
    } catch {
      console.warn('[inline-quick-add] Could not parse variant data', overlay);
      return;
    }

    const sizeIndex    = parseInt(overlay.dataset.sizeIndex  ?? '-1', 10);
    const colorIndex   = parseInt(overlay.dataset.colorIndex ?? '-1', 10);
    const optionsCount = parseInt(overlay.dataset.optionsCount ?? '1', 10);

    /** Tracks the currently selected option value per option-index slot. */
    const selection = {};

    const feedbackEl = overlay.querySelector('.inline-quick-add__feedback');

    overlay.addEventListener('click', handleButtonClick);

    /**
     * @param {MouseEvent} event
     */
    function handleButtonClick(event) {
      const btn = /** @type {HTMLElement} */ (event.target.closest('.inline-quick-add__btn'));
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      event.stopPropagation(); // prevent card link navigation

      const optionIndex = parseInt(btn.dataset.optionIndex, 10);
      const optionValue = btn.dataset.optionValue;

      // Toggle selection for this option slot
      if (selection[optionIndex] === optionValue) {
        // Deselect
        delete selection[optionIndex];
        btn.dataset.selected = 'false';
      } else {
        // Deselect any previously selected button in this group
        overlay
          .querySelectorAll(`.inline-quick-add__btn[data-option-index="${optionIndex}"]`)
          .forEach((b) => { b.dataset.selected = 'false'; });

        selection[optionIndex] = optionValue;
        btn.dataset.selected = 'true';
      }

      // Determine how many selectable option slots are shown in this overlay
      const visibleSlots = [];
      if (sizeIndex  >= 0) visibleSlots.push(sizeIndex);
      if (colorIndex >= 0) visibleSlots.push(colorIndex);

      const allVisibleSelected = visibleSlots.every((idx) => selection[idx] !== undefined);

      if (!allVisibleSelected) {
        // Not enough info yet — clear any old feedback
        setFeedback(overlay, feedbackEl, '');
        return;
      }

      // Try to resolve a specific variant
      const resolvedVariant = resolveVariant(variants, selection, visibleSlots, optionsCount);

      if (!resolvedVariant) {
        // Cannot determine a unique variant — open the quick-add modal instead
        fallbackToModal(overlay);
        return;
      }

      if (!resolvedVariant.available) {
        setFeedback(overlay, feedbackEl, 'Sold out');
        return;
      }

      addToCart(overlay, feedbackEl, resolvedVariant.id, btn);
    }
  }

  /**
   * Resolves a single variant from the current selection.
   *
   * When not all product options are shown in the overlay (e.g. a product has
   * Size + Color + Length but only Size is shown), we look for the first
   * available variant that matches the shown selections — mirroring Shopify's
   * "first available" behaviour.
   *
   * @param {Array}  variants
   * @param {Object} selection     - { optionIndex: value, ... }
   * @param {number[]} visibleSlots - option indices that are shown in overlay
   * @param {number} optionsCount  - total number of options on the product
   * @returns {{ id: number, available: boolean } | null}
   */
  function resolveVariant(variants, selection, visibleSlots, optionsCount) {
    const candidates = variants.filter((v) =>
      visibleSlots.every((idx) => v.options[idx] === selection[idx])
    );

    if (candidates.length === 0) return null;

    // If all options are covered by the visible slots, find the exact match
    if (visibleSlots.length >= optionsCount) {
      return candidates[0] ?? null;
    }

    // Otherwise return the first available candidate (partial selection)
    return candidates.find((v) => v.available) ?? candidates[0] ?? null;
  }

  /**
   * POSTs to /cart/add.js and handles success/error feedback.
   * @param {HTMLElement} overlay
   * @param {HTMLElement} feedbackEl
   * @param {number}      variantId
   * @param {HTMLElement} clickedBtn
   */
  async function addToCart(overlay, feedbackEl, variantId, clickedBtn) {
    overlay.dataset.loading = 'true';
    setFeedback(overlay, feedbackEl, '');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${response.status}`);
      }

      // Success
      clickedBtn.dataset.added = 'true';
      setFeedback(overlay, feedbackEl, 'Added!');

      // Notify cart drawer / cart icon
      document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, { bubbles: true }));

      // Reset after 1.5 s
      setTimeout(() => {
        clickedBtn.dataset.added    = 'false';
        clickedBtn.dataset.selected = 'false';
        // Clear selection state
        Object.keys(overlay.__iqaSelection ?? {}).forEach((k) => { delete overlay.__iqaSelection[k]; });
        setFeedback(overlay, feedbackEl, '');
        // Deselect all buttons
        overlay.querySelectorAll('.inline-quick-add__btn').forEach((b) => { b.dataset.selected = 'false'; });
      }, 1500);

    } catch (err) {
      console.error('[inline-quick-add] Add to cart failed:', err);
      setFeedback(overlay, feedbackEl, 'Error — try again');

      setTimeout(() => {
        setFeedback(overlay, feedbackEl, '');
      }, 2500);
    } finally {
      overlay.dataset.loading = 'false';
    }
  }

  /**
   * Opens the existing quick-add modal by finding and clicking the
   * quick-add-component's choose button within the same product card.
   * Falls back gracefully if the component is not present.
   * @param {HTMLElement} overlay
   */
  function fallbackToModal(overlay) {
    const card = overlay.closest('product-card, .product-card');
    if (!card) return;

    const chooseBtn = card.querySelector('.quick-add__button--choose');
    if (chooseBtn) {
      chooseBtn.click();
    } else {
      // Last resort: navigate to the product page link
      const productLink = card.querySelector('a.product-card__link, a[href*="/products/"]');
      if (productLink) window.location.href = productLink.href;
    }
  }

  /**
   * Sets accessible feedback text in the overlay feedback region.
   * @param {HTMLElement} overlay
   * @param {HTMLElement} feedbackEl
   * @param {string}      message
   */
  function setFeedback(overlay, feedbackEl, message) {
    if (feedbackEl) feedbackEl.textContent = message;
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  // Run on DOMContentLoaded and also after Shopify section re-renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOverlays);
  } else {
    initOverlays();
  }

  // Re-init after Shopify section renders (theme editor / infinite scroll)
  document.addEventListener('shopify:section:load', initOverlays);
  document.addEventListener('shopify:block:select', initOverlays);

  // Re-init after cart updates that may have caused a section reload
  document.addEventListener(CART_UPDATE_EVENT, () => {
    // Small delay to allow any DOM morphing to settle
    setTimeout(initOverlays, 300);
  });
})();
